import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { AppButton } from '@/components'
import { useSavingsStore } from '@/store/useSavingsStore'
import { useUserStore } from '@/store/useUserStore'
import {
  useGetFeaturesSettings,
  useGetSavingGoal,
  type WalletCurrency,
} from '@/domain'
import { currencyUtils } from '@/utils'

const { parseAmountToFloat } = currencyUtils

export const TransferOutButtonConfirmation: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const fiatAmount = useSavingsStore(
    (state) => state.goalTransferOut.fiatAmount,
  )
  const goalTransferOut = useSavingsStore((state) => state.goalTransferOut)
  const setGoalTransferOut = useSavingsStore(
    (state) => state.setGoalTransferOut,
  )

  const user = useUserStore((s) => s.userAuthenticated)
  const displayCurrency = user?.display_currency || 'USD'

  // Получаем данные savings goal для валидации
  const { data: savingGoal, isLoading: isLoadingSavingGoal } = useGetSavingGoal(
    {
      saving_goal_id: goalTransferOut.saving_goal_id,
    },
  )

  // Получаем настройки функций
  const { data: featureSettings, isLoading: isLoadingFeatureSettings } =
    useGetFeaturesSettings()

  const handleTransferOut = async () => {
    if (isLoadingSavingGoal || isDisabled) return

    // Валидация суммы как в React Native
    const isValid = validateFiatAmount()
    if (!isValid) {
      // Сброс суммы при невалидном значении
      setGoalTransferOut({
        fiatAmount: {
          amount: '0',
          code: displayCurrency || 'USD',
        },
        cryptoAmount: {
          amount: '0',
          code: 'USDT',
        },
      })
      return
    }

    // Переходим на страницу подтверждения
    navigate('/savings/transfer-out/confirm-transfer')
  }

  // Проверяем, что сумма введена и валидна
  const fiatAmountValue = parseAmountToFloat({
    amount: fiatAmount.amount,
    decimalSeparator: '.',
  })

  // Валидация суммы против баланса savings goal (как в React Native)
  const validateFiatAmount = () => {
    if (!savingGoal) return false

    const maxAmount = parseAmountToFloat({
      amount: savingGoal.staked_amount.fiat_amount || '0',
      decimalSeparator: '.',
    })

    const minAmount = featureSettings?.savings?.min_amount || 1

    return fiatAmountValue >= minAmount && fiatAmountValue <= maxAmount
  }

  const isDisabled =
    fiatAmountValue <= 0 ||
    isLoadingFeatureSettings ||
    isLoadingSavingGoal ||
    !savingGoal ||
    !validateFiatAmount()

  return (
    <AppButton
      label={t('form:actions.transferOut')}
      onPress={handleTransferOut}
      disabled={isDisabled}
      isLoading={isLoadingFeatureSettings || isLoadingSavingGoal}
    />
  )
}
